// Vendor
import { FC } from 'react';

// Components
import { Card } from 'src/components/atoms/Card';
import Gap from 'src/components/atoms/Gap';
import { Row } from 'src/components/atoms/Row';
import Text, { TextVariant } from 'src/components/atoms/Text';
import { ProofListContainer } from 'src/components/folders/containers';
import { AutoSavingNoteForm } from 'src/components/molecules/AutoSavingNoteForm';
import { ResultMessage, ResultMessageType } from 'src/components/molecules/ResultMessage';
import { ShowState } from 'src/components/molecules/ShowState';
import EntriesMultiTabView from 'src/components/organisms/EntriesMultiTabView';
import { EntryStatusBar } from '../EntryStatusBar';
import { MetadataModal } from '../MetadataModal';
import { SplitEntryModal } from '../modals';
import { RequestDocumentsModal } from '../modals/RequestDocumentsModal';
import CalculationNotesCard from './CalculationNotesCard';

// Hooks
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { useEntryUpdateNote, useRequestDocuments } from 'src/features/entries/hooks';
import { useCalculationDispute, useCalculationNotes } from 'src/features/incomeVerification/hooks';
import { useProofById } from 'src/features/proofs/hooks';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { useFolderPage } from '../FolderPageContext';

// Types
import { SubmissionsTabContentProps, SubmissionsTabProps } from './types';

// Styles
import { Hint, NoteField, NotesCard, NotesLayout } from './styles';

import { FeatureFlagKey } from 'src/ts/enums';
import { IProof } from 'src/ts/interfaces';
import SendReportFraudModal from '../modals/SendReportFraudModal';

export const EntryListContent: FC<SubmissionsTabContentProps> = ({
  entry,
  isButtonDisabled,
  isAccountRepresentative,
  canSeeCalculationNotes
}) => {
  const { setShowSplitEntryModal } = useFolderPage();
  const { onUpdateNote } = useEntryUpdateNote();
  const { onDisputeCalculation, isDisputeLoading } = useCalculationDispute(entry?.folder_id);
  const { calculationNotes } = useCalculationNotes({
    entryId: entry?.id,
    enabled: canSeeCalculationNotes
  });
  return (
    <Row direction="column" gap={1}>
      <Row.Col>
        <EntryStatusBar
          result={entry?.result}
          income={entry?.income_calculation}
          suggestedRuling={entry?.suggested_ruling}
          disableButton={isButtonDisabled}
          entryId={entry?.id}
          onRemoveFromFolder={() => setShowSplitEntryModal?.(true)}
          disputeMetadata={entry?.income_verification_disputable}
          onIncomeCalculationDispute={async () =>
            await onDisputeCalculation(entry?.id, entry?.income_verification_review_id)
          }
          isDisputesButtonDisabled={isDisputeLoading}
          offerLetterPendingEmployerVerification={entry?.offer_letter_pending_employer_verification}
          pendingSpecialtyReview={entry?.pending_specialty_review}
        />
      </Row.Col>
      {entry?.pending_specialty_review && (
        <Row.Col>
          <ResultMessage
            type={ResultMessageType.warning}
            title="Pending Specialty Review"
            message="Income is under specialty review and will appear once the review is complete."
          />
        </Row.Col>
      )}
      <Row.Col>
        <NotesLayout hasCalculationNotes={calculationNotes.length > 0}>
          <NotesCard>
            <Text variant={TextVariant.h4}>Application notes</Text>
            <NoteField>
              <AutoSavingNoteForm
                name="entryNote"
                placeholder="Application notes here"
                defaultValue={entry?.note}
                isReadonly={isAccountRepresentative}
                onSubmit={(value) => onUpdateNote?.(entry.id, value?.entryNote)}
              />
            </NoteField>
            {!isAccountRepresentative && <Hint>Saves as you type</Hint>}
          </NotesCard>
          {calculationNotes.length > 0 && <CalculationNotesCard notes={calculationNotes} />}
        </NotesLayout>
      </Row.Col>
      <Row.Col>
        <ProofListContainer />
      </Row.Col>
    </Row>
  );
};

const EntryList: FC<Pick<SubmissionsTabProps, 'entries'>> = ({ entries }) => {
  const { onChangeSelectedEntry: handleSelectedSubmissionsTab } = useFolderPage();
  const { isAdmin, isAccountRepresentative, isAnalyst, isFDEManager } = useUserRoles();
  const isCalculationNoteEnabled = useFeatureFlag(
    FeatureFlagKey.ITL_3287_SPECIALTY_CALCULATION_NOTE
  );
  const canSeeCalculationNotes =
    isCalculationNoteEnabled &&
    Boolean(isAdmin || isAccountRepresentative || isAnalyst || isFDEManager);

  if (!entries?.length) {
    return (
      <Card>
        <Gap height={2.75} />
        <ShowState
          variant="empty"
          type="application"
          message="There are no applications <br /> available to show"
        />
      </Card>
    );
  }

  return (
    <EntriesMultiTabView
      entries={entries}
      onTabSelect={(index) => handleSelectedSubmissionsTab?.(entries?.[index]?.id)}
      tabContentChildren={(entry) => (
        <EntryListContent
          entry={entry}
          isButtonDisabled={entries?.length === 1}
          isAccountRepresentative={isAccountRepresentative ?? false}
          canSeeCalculationNotes={canSeeCalculationNotes}
        />
      )}
    />
  );
};

const MetadataModalContainer = () => {
  const { selectedProofId, showMetadataModal, setShowMetadataModal } = useFolderPage();
  const { proof } = useProofById(selectedProofId as string);
  return (
    <MetadataModal
      extracted_meta={proof?.extracted_meta}
      test_meta_data_flags={proof?.test_meta_data_flags}
      showModal={showMetadataModal}
      setShowModal={setShowMetadataModal}
    />
  );
};

const EntryModals = () => {
  const { selectedEntryId, showRequestDocumentsModal, setShowRequestDocumentsModal } =
    useFolderPage();
  const { onRequestDocuments } = useRequestDocuments();
  return (
    <>
      <MetadataModalContainer />
      <RequestDocumentsModal
        showModal={showRequestDocumentsModal}
        setShowModal={setShowRequestDocumentsModal}
        onConfirm={(message: string, proofs?: IProof[]) => {
          setShowRequestDocumentsModal?.(false);

          return onRequestDocuments(selectedEntryId, message, proofs);
        }}
      />
      <SplitEntryModal />
      <SendReportFraudModal text="Are you sure you want to continue?" />
    </>
  );
};

const SubmissionsTab: FC<SubmissionsTabProps> = ({ entries }) => {
  return (
    <>
      <EntryModals />
      <Row direction="column" gap={1}>
        <Row.Col>
          <Text variant={TextVariant.h3}>Submissions by Date</Text>
        </Row.Col>
        <Row.Col>
          <EntryList entries={entries} />
        </Row.Col>
      </Row>
    </>
  );
};

export default SubmissionsTab;
